import axios from 'axios';
import { baseURL, fixedDateRangeValues, MAX_FETCH_LIMIT_FOR_CSV_EXPORT, socialAnalyticsProxyUrl } from '../event-analytics-constants';
import { EventAnalyticsState } from '../../../contexts/eventanalytics/event-analytics';
import { API_RESPONSE_TYPE, BracketReference, CsvDataObject } from '../types';
import { DateTime } from 'luxon';
import { dateTimeFormatUTC } from '../date-range-options';
import { YoutubeContextType } from '../../../contexts/eventanalytics/youtube';

export const fetchLineChartData = async (
  EventAnalyticsState: EventAnalyticsState,
  timeWindowValue: string,
): Promise<API_RESPONSE_TYPE> => {
  const {
    customerId,
    token,
    eventId,
    dateRange: { startDate, endDate },
  } = EventAnalyticsState;
  const pathValue = eventId !== null ? 'liveViews' : 'views';
  timeWindowValue = eventId !== null ? 'minute' : timeWindowValue;
  const url = `${baseURL}/${customerId}/youTube/statistics/${timeWindowValue}/${pathValue}`;
  try {
    const { data } = await axios.get(url, {
      headers: token,
      params: {
        startDate: DateTime.fromISO(startDate).startOf('day').toFormat(dateTimeFormatUTC),
        endDate: DateTime.fromISO(endDate).endOf('day').toFormat(dateTimeFormatUTC),
        ...(eventId && { eventId }),
      },
    });
    return data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

export const fetchLineChartOnDemandData = async (
  EventAnalyticsState: EventAnalyticsState,
  youtubeAnalyticsState: YoutubeContextType,
  timeWindowValue: string,
): Promise<API_RESPONSE_TYPE> => {
  const {
    customerId,
    token,
    eventId,
    dateRange: { startDate, endDate },
  } = EventAnalyticsState;
  const url = `${socialAnalyticsProxyUrl}/${customerId}/youTubeOnDemand/statistics/${timeWindowValue}/${youtubeAnalyticsState.page}`;
  try {
    const { data } = await axios.get(url, {
      headers: token,
      params: {
        startDate: DateTime.fromISO(startDate).startOf('day').toFormat(dateTimeFormatUTC),
        endDate: DateTime.fromISO(endDate).endOf('day').toFormat(dateTimeFormatUTC),
        segmentBy: youtubeAnalyticsState.groupBy,
        ...(eventId && { eventId }),
      },
    });
    return data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

export const fetchSummaryStats = async (
  EventAnalyticsState: EventAnalyticsState,
  page: string,
): Promise<BracketReference<number | null>[]> => {
  const {
    customerId,
    token,
    eventId,
    dateRange: { startDate, endDate },
    dateRangeOption,
  } = EventAnalyticsState;
  const liveAPI = `${baseURL}/${customerId}/youTube/statistics/summary`;
  const onDemandAPI = `${socialAnalyticsProxyUrl}/${customerId}/youTubeOnDemand/statistics/summary`;
  const summaryStatsAPI = page !== 'liveViews' ? onDemandAPI : liveAPI;
  const dateRangeValue = dateRangeOption.value.toString();
  const isFullMonth = fixedDateRangeValues.includes(dateRangeValue);
  try {
    const { data } = await axios.get(summaryStatsAPI, {
      headers: token,
      params: {
        startDate: DateTime.fromISO(startDate).startOf('day').toFormat(dateTimeFormatUTC),
        endDate: DateTime.fromISO(endDate).endOf('day').toFormat(dateTimeFormatUTC),
        isFullMonth,
        ...(eventId && { eventId }),
      },
    });
    return data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const fetchDeviceTypeData = async (
  EventAnalyticsState: EventAnalyticsState,
  groupBy: string,
): Promise<API_RESPONSE_TYPE> => {
  const {
    customerId,
    token,
    eventId,
    dateRange: { startDate, endDate },
  } = EventAnalyticsState;
  const url = `${socialAnalyticsProxyUrl}/${customerId}/youTubeOnDemand/statistics/views/deviceType`;
  groupBy = groupBy === 'device_type' ? 'none' : groupBy;
  try {
    const { data } = await axios.get(url, {
      headers: token,
      params: {
        startDate: DateTime.fromISO(startDate).startOf('day').toFormat(dateTimeFormatUTC),
        endDate: DateTime.fromISO(endDate).endOf('day').toFormat(dateTimeFormatUTC),
        segmentBy: groupBy,
        ...(eventId && { eventId }),
      },
    });
    return data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

export const exportCSVData = async (
  EventAnalyticsState: EventAnalyticsState,
  offset1?: string | number | undefined,
  offset2?: string | number | undefined
): Promise<CsvDataObject[]> => {
  const {
    customerId,
    eventId,
    token,
    dateRange: { startDate, endDate },
  } = EventAnalyticsState;
  const url = `${baseURL}/${customerId}/youTube/export`;
  let offset;
  if (offset1 && offset2) {
    offset = `${offset1},${offset2}`;
  }
  const defaultParams = {
    startDate: DateTime.fromISO(startDate).startOf('day').toFormat(dateTimeFormatUTC),
    endDate: DateTime.fromISO(endDate).endOf('day').toFormat(dateTimeFormatUTC),
    max: MAX_FETCH_LIMIT_FOR_CSV_EXPORT,
  };
  const params = {
    ...defaultParams,
    ...(eventId && { eventId }),
    ...(offset && { offset }),
  };
  try {
    const { data } = await axios.get(url, {
      headers: token,
      params,
    });
    return data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const exportCsvOnDemand = async (EventAnalyticsState: EventAnalyticsState) => {
  const {
    customerId,
    eventId,
    token,
    dateRange: { startDate, endDate },
  } = EventAnalyticsState;
  const url = `${socialAnalyticsProxyUrl}/${customerId}/youTubeOnDemand/export`;
  const defaultParams = {
    startDate: DateTime.fromISO(startDate).startOf('day').toFormat(dateTimeFormatUTC),
    endDate: DateTime.fromISO(endDate).endOf('day').toFormat(dateTimeFormatUTC),
  };
  const params = {
    ...defaultParams,
    ...(eventId && { eventId }),
  };
  try {
    const { data } = await axios.get(url, {
      headers: token,
      params,
    });
    return data;
  } catch (error) {
    console.error(error);
    return [];
  }
}
